import { useState } from 'react';
import { Box, Grid, Tooltip, Typography } from '@material-ui/core';
import { useUserStore } from '@roc/feature-app-core';
import { formatCurrency, formatNumberWithCommas, formatPercentage } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import { insertIf, LoanSubType } from '@roc/feature-utils';
import {
  LoanDetailsCustomCard,
  LoanDetailsInfoItem,
  useLoanDetailsInfoItemStyles,
} from '../loanDetailsCustomCard';
import { useLoanFlags } from '../../hooks/useLoanFlags';
import { useStore } from '@roc/client-portal-shared/hooks';


const formatPoints = value => {
  return Number((value || 0).toFixed(4));
};

type loanSummaryProps = {
  displayCreateAppraisalOrderButton: boolean,
  classes: any,
  loanStore: any,
  loanDetails: any,
  showCreateTask: boolean
  allowLoanPricing: boolean
  lenderPoints: any,
  isInternal: boolean
  isOffHudBroker: boolean
  isModalView?: boolean
  showPropertyDialog?: any,
  setShowPropertyDialog?: any,
  setProperty?: any,
  property?: any
  submissionVsUnderwritingStore?: any
}

export const ManualQuoteSummaryCard = observer(() => {
  const {
    allowLoanPricing,
  } = useLoanFlags();

  const { loanStore, globalStore } = useStore();
  const { userStore } = useUserStore();
  const { loanDetails } = loanStore;
  const classes = useLoanDetailsInfoItemStyles();
  const isInternal = globalStore?.lenderInfo?.isInternal;
  const showCreateTask = true;
  const isOffHudBroker = loanDetails?.referralFeePct && loanDetails?.referralFeePct > 0 ? true : false;


  const lenderPoints = Math.max(
    (loanDetails?.pointsIn ?? 0) - (loanDetails?.rocPointsIn ?? 0),
    0
  );
  return (
    <LoanDetailsCustomCard contentStyle={{
      minWidth: 1100
    }} cardContentProps={{
      paperNoShadow: true,
      style: {
        overflow: 'auto'
      }
    }}>
      <CardComponent
        displayCreateAppraisalOrderButton={false}
        classes={classes}
        loanStore={loanStore}
        loanDetails={loanDetails}
        showCreateTask={showCreateTask}
        allowLoanPricing={allowLoanPricing}
        lenderPoints={lenderPoints}
        isInternal={isInternal}
        isOffHudBroker={isOffHudBroker} />
    </LoanDetailsCustomCard>
  );
});

export const CardComponent = observer(({ classes, loanStore, loanDetails, allowLoanPricing, lenderPoints, isInternal, isOffHudBroker, isModalView }: loanSummaryProps) => {

  return (<>
    <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 20 }}>
      <Typography variant="h5">
        Loan Summary
      </Typography>
    </Grid>
    <Grid item xs={12} style={{ display: 'flex', marginBottom: '20px' }}>
      <Grid item xs={isModalView ? 12 : 3} md={3} style={{ position: 'relative', paddingRight: 24 }}>
        <Grid container spacing={0} style={{ position: 'relative', paddingTop: 0 }} className={classes.amountHighligher}>
          <Grid item xs={12}>
            <LoanDetailsInfoItem
              value={formatCurrency(loanDetails?.amount, 0)}
              label={loanDetails?.loanSubType}
              valueStyle={{
                fontSize: 34,
                fontWeight: 400
              }}
              py={0}
              labelStyle={{
                color: "#939494",
                textTransform: 'uppercase',
                letterSpacing: 'normal',
                fontSize: 11,
                fontWeight: 'bold'

              }}
              labelComponent={<>
                <Typography className={classes.loanSummaryLabelStyle}>
                  {loanDetails?.loanSubType}
                </Typography>
              </>}
            />
          </Grid>
          <Grid item xs={12}>
            <Box display={'flex'} height={'100%'}>
              <Box pr={2}>
                <LoanDetailsInfoItem
                  boxStyle={{
                    paddingTop: 2
                  }}
                  value={formatCurrency(loanDetails?.initialLoanAmount, 0)}
                  labelStyle={{
                    color: "#939494",
                    textTransform: 'uppercase',
                    fontSize: 11,
                    fontWeight: 'bold'
                  }}
                  valueStyle={{
                    fontWeight: 500
                  }}
                  label={`Initial`}
                  secondaryLabelComponent={
                    <Box fontSize={12}>
                      <span className={classes.loanSummaryLabelStyle}>
                        {`${loanDetails?.aggregateLoanToCurrentCostInitial}% of `}
                      </span>
                      <Tooltip title={`Acquisition Price`} arrow>
                        <span className={classes.loanSummaryLabelStyle} style={{
                          borderBottom: '1px dashed #bbb'
                        }}>
                          {formatCurrency(loanDetails?.calculatedLoanData?.aggregatePurchasePrice, 0)}
                        </span>
                      </Tooltip>
                    </Box>
                  }
                />
              </Box>
              <Box pr={2} my={1} borderLeft={'1px solid #bbb'}>
              </Box>
              <Box pr={2}>
                <LoanDetailsInfoItem
                  boxStyle={{
                    paddingTop: 2
                  }}
                  value={formatCurrency(loanDetails?.constructionHoldback, 0)}
                  label={`Holdback`}
                  valueStyle={{
                    fontWeight: 500
                  }}
                  labelStyle={{
                    color: "#939494",
                    textTransform: 'uppercase',
                    letterSpacing: 'normal',
                    fontSize: 11,
                    fontWeight: 'bold'
                  }}
                  secondaryLabelComponent={
                    <Box fontSize={12}>
                      <span className={classes.loanSummaryLabelStyle}>
                        {`${loanDetails?.constructionHoldbackToRenovationBudgetPercent}% of `}
                      </span>
                      <Tooltip className={classes.loanSummaryLabelStyle} title={`Total Renovation Budget`} arrow>
                        <span style={{
                          borderBottom: '1px dashed #bbb'
                        }}>
                          {formatCurrency(loanDetails?.calculatedLoanData?.aggregateRenovationBudget, 0)}
                        </span>
                      </Tooltip>
                    </Box>
                  }
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={9}>
        <Grid container spacing={0} style={{
          position: 'relative'
        }}>
          {allowLoanPricing &&
            <Grid item xs={isModalView ? 12 : 3} md={3}>
              <LoanDetailsInfoItem
                value={`${formatPoints((loanDetails?.brokerPointsIn ?? 0) +
                  (lenderPoints ?? 0) +
                  (loanDetails?.rocPointsIn ?? 0) + (loanDetails?.referralFeePct ?? 0))
                  } points`}
                labelStyle={{
                  color: "#939494",
                  textTransform: 'uppercase',
                  letterSpacing: 'normal',
                  fontSize: 11,
                  fontWeight: 'bold'
                }}
                label={
                  <>
                    {isInternal || isOffHudBroker
                      ? (isOffHudBroker
                        ? `${formatPoints(loanDetails?.referralFeePct ?? 0)} Off Hud Broker | `
                        : `${formatPoints(loanDetails?.brokerPointsIn ?? 0)} On Hud Broker | `
                      )
                      : `${formatPoints(loanDetails?.brokerPointsIn ?? 0)} Broker | `}
                    {formatPoints(lenderPoints ?? 0)} Lender <br />
                    {formatPoints(loanDetails?.rocPointsIn ?? 0)} Capital Provider
                  </>
                }
              />
            </Grid>
          }
          <Grid item xs={isModalView ? 12 : 3} md={3}>
            <LoanDetailsInfoItem
              value={formatCurrency(loanDetails?.interestReserve, 2)}
              label="Interest Reserve"
              labelStyle={{
                color: "#939494",
                textTransform: 'uppercase',
                letterSpacing: 'normal',
                fontSize: 11,
                fontWeight: 'bold'
              }}
            />
          </Grid>
          {loanStore?.shouldShowAppraisalLoanLevel && <Grid item xs={isModalView ? 12 : 3} md={3}>
            <LoanDetailsInfoItem
              value={formatCurrency(
                loanDetails?.properties[0]?.afterRepairValue,
                0
              )}
              label={`ARV (${loanDetails?.calculatedLoanData?.aggregateAfterRepairLoanToValue}% ARLTV)`}
              labelStyle={{
                color: "#939494",
                textTransform: 'uppercase',
                letterSpacing: 'normal',
                fontSize: 10,
                fontWeight: 'bold'
              }}
            />
          </Grid>}
          <Grid item xs={isModalView ? 12 : 3} md={3}>
            <LoanDetailsInfoItem
              value={`${loanDetails?.duration} Months`}
              label="Loan Term"
              labelStyle={{
                color: "#939494",
                textTransform: 'uppercase',
                letterSpacing: 'normal',
                fontSize: 10,
                fontWeight: 'bold'
              }}
            />
          </Grid>
          <Grid item xs={isModalView ? 12 : 3} md={3}>
            <LoanDetailsInfoItem
              value={loanDetails?.loanPurpose ? loanDetails?.loanPurpose : '-'}
              label='Loan Purpose'
              labelStyle={{
                color: "#939494",
                textTransform: 'uppercase',
                letterSpacing: 'normal',
                fontSize: 10,
                fontWeight: 'bold'
              }}
            />
          </Grid>
          <Grid item xs={isModalView ? 12 : 3} md={3}>
            <LoanDetailsInfoItem
              value={loanDetails?.rate ? `${loanDetails?.rate}%` : '-'}
              label='Interest Rate'
              labelStyle={{
                color: "#939494",
                textTransform: 'uppercase',
                letterSpacing: 'normal',
                fontSize: 10,
                fontWeight: 'bold'
              }}
            />
          </Grid>
          <Grid item xs={isModalView ? 12 : 3} md={3}>
            <LoanDetailsInfoItem
              value={loanDetails?.pledgeOfShares ? 'Yes' : 'No'}
              label="Pledge of Shares"
              labelStyle={{
                color: "#939494",
                textTransform: 'uppercase',
                letterSpacing: 'normal',
                fontSize: 10,
                fontWeight: 'bold'
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  </>)
})
